import {
  UserAgreementHistory,
  UserAgreementManagement,
  UserAgreementManagementKeys,
} from '@ag-common-lib/lib/models/utils/user-agreement-management.model';
import { Agent } from '@ag-common-lib/public-api';
import { dateFromTimestamp } from 'ag-common-svc/lib/utils/date-from-timestamp';
import { BaseModel, BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { Policy, PolicyKeys } from '@ag-common-lib/lib/models/utils/policy.model';
import { AgentNameByDbIdePipe } from 'ag-common-svc/shared/pipes/agent.pipe';
import { combineLatest, from, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { PolicySigners, PolicySignersKeys } from '@ag-common-lib/lib/models/utils/policy-signers.model';
import { AgentSearchService } from 'ag-common-svc/lib/services/agent-search';

export const userAgreementFromFirestore = (data: UserAgreementManagement): any => {
  return Object.assign({}, data, {
    [UserAgreementManagementKeys.effectiveDate]: dateFromTimestamp(data?.effectiveDate),
  });
};

export const userAgreementLogHistoryFromFirestore = (data: UserAgreementHistory): any => {
  return Object.assign({}, data, {
    [BaseModelKeys.updatedDate]: dateFromTimestamp(data?.[BaseModelKeys.updatedDate]),
  });
};

export const policiesFromFirestore = (data: Policy) => {
  return Object.assign({}, data, {
    [BaseModelKeys.createdDate]: dateFromTimestamp(data?.[BaseModelKeys.createdDate]),
    [PolicyKeys.effectiveDate]: dateFromTimestamp(data?.[PolicyKeys.effectiveDate]),
  });
};

export const policiesSignersFromFirestore = (data: PolicySigners) => {
  return Object.assign({}, data, {
    [BaseModelKeys.createdDate]: dateFromTimestamp(data?.[BaseModelKeys.createdDate]),
    [BaseModelKeys.updatedDate]: dateFromTimestamp(data?.[BaseModelKeys.updatedDate]),
  });
};

/**
 * Updates an array of items with createdByAgent and modifiedByAgent metadata.
 */
export function updateWithAgentMetadata<T extends BaseModel>(
  items: T[],
  agentSearchService: AgentSearchService,
  showPrimaryEmail = true,
): Observable<T[]> {
  // One label, however the agent was resolved. Each of these used to open its own
  // Firestore document listener, per row, for the author and the editor alike.
  const toAgentLabel = (data: Agent | null): string => {
    if (!data) {
      return '';
    }

    const fullAgentName = [data?.p_agent_first_name, data?.p_agent_last_name].filter(Boolean).join(' ');
    const primaryEmail = data?.p_email ?? '';

    return showPrimaryEmail && primaryEmail ? `${fullAgentName} (${primaryEmail})` : fullAgentName;
  };
  if (!items?.length) {
    return of([]);
  }

  const itemsWithAgentsMetadata$ = items.map(item => {
    const createdByAgent$ = item?.[BaseModelKeys.createdByAgentDbId]
      ? from(agentSearchService.getById(item[BaseModelKeys.createdByAgentDbId])).pipe(map(toAgentLabel))
      : of('');

    const modifiedByAgent$ = item?.[BaseModelKeys.updatedByAgentDbId]
      ? from(agentSearchService.getById(item[BaseModelKeys.updatedByAgentDbId])).pipe(map(toAgentLabel))
      : of('');

    return combineLatest([createdByAgent$, modifiedByAgent$]).pipe(
      map(([createdByAgent, modifiedByAgent]) => ({
        ...item,
        createdByAgent,
        modifiedByAgent,
      })),
    );
  });

  return combineLatest(itemsWithAgentsMetadata$);
}
